import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCampaignsRequest, type Campaign } from '../../redux/slices/campaignsSlice';
import { triggerWorkflowRequest } from '../../redux/slices/workflowTriggerSlice';
import { setRestaurantId } from '../../redux/slices/cloverSlice';
import type { RootState } from '../../redux/store';
import { useParams, useSearchParams } from 'react-router-dom';
import ENDPOINTS from '../../api/endPoints';
import { CampaignProcessingView } from './CampaignProcessingView';

// Helper function to get user-friendly progress messages
const getProgressMessage = (progress: number): string => {
  if (progress >= 100) return 'Your campaign is ready!';
  if (progress >= 90) return 'Finalizing your campaign assets...';
  if (progress >= 70) return 'Refining campaign visuals and messaging...';
  if (progress >= 50) return 'Exploring alternative creative directions...';
  if (progress >= 30) return 'Creating your first flyer concept...';
  if (progress >= 25) return 'Analyzing campaign requirements...';
  return 'Initializing system-generated ideas...';
};

const GetStarted: React.FC = () => {
  const { navigate } = useRestaurantNav();
  const dispatch = useDispatch();
  const { restaurantId: urlRestaurantId } = useParams<{ restaurantId: string }>();
  const [searchParams] = useSearchParams();

  const { loading: workflowLoading, success: workflowSuccess, data: workflowData } = useSelector((state: RootState) => state.workflowTrigger);
  const { data: campaigns, loading: campaignsLoading } = useSelector((state: RootState) => state.campaigns);
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  const [showGenerateButton, setShowGenerateButton] = useState(false);
  const [hasRequiredAuth, setHasRequiredAuth] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState('Initializing system-generated ideas...');
  const [visibleCampaigns, setVisibleCampaigns] = useState<Campaign[]>([]);
  const [newlyAddedIndex, setNewlyAddedIndex] = useState<number | null>(null);

  // Extract account_id and token from query params and store in localStorage
  useEffect(() => {
    const accountIdFromQuery = searchParams.get('account_id');
    const token = searchParams.get('token');

    if (accountIdFromQuery) {
      localStorage.setItem('account_id', accountIdFromQuery);
    }

    if (token) {
      localStorage.setItem('token', token);
    }
  }, [searchParams]);

  useEffect(() => {
    if (urlRestaurantId) {
      localStorage.setItem('restaurantId', urlRestaurantId);
      dispatch(setRestaurantId(urlRestaurantId));
    }
  }, [urlRestaurantId, dispatch]);

  // Check if restaurantId and (account_id OR token) are present
  useEffect(() => {
    const storedRestaurantId = localStorage.getItem('restaurantId');
    const storedAccountId = localStorage.getItem('account_id');
    const storedToken = localStorage.getItem('token');

    if (storedRestaurantId && (storedAccountId || storedToken)) {
      setHasRequiredAuth(true);
    } else {
      setHasRequiredAuth(false);
    }
  }, [restaurantId, searchParams]);

  // Fetch campaigns on component mount
  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchCampaignsRequest());
    }
  }, [dispatch, restaurantId]);

  // Check campaigns data and navigate accordingly (only when NOT processing)
  useEffect(() => {
    if (isProcessing) return;
    
    if (!campaignsLoading && campaigns !== undefined) {
      if (campaigns.length > 0) {
        navigate('/dashboard');
      } else {
        setShowGenerateButton(true);
      }
    }
  }, [campaigns, campaignsLoading, navigate, isProcessing]);

  // Handle workflow trigger success - start processing inline
  useEffect(() => {
    if (workflowSuccess && workflowData) {
      const requestId = workflowData.requestId || workflowData.taskId;
      if (requestId) {
        setIsProcessing(true);
        
        const sseUrl = ENDPOINTS.sseMonitor(requestId);
        const eventSource = new EventSource(sseUrl);

        eventSource.onopen = () => {
          console.log('SSE Connection opened for workflow monitoring');
        };

        eventSource.addEventListener('connected', (event: any) => {
          try {
            const data = JSON.parse(event.data);
            console.log('Connected:', data.message);
          } catch (error) {
            console.error('Error parsing connected event:', error);
          }
        });

        eventSource.addEventListener('inprogress', (event: any) => {
          try {
            const data = JSON.parse(event.data);
            if (data.percentCompleted !== undefined) {
              const prevProgress = progress;
              
              // Auto-complete at 90% - change to 100%
              const updatedProgress = data.percentCompleted >= 90 ? 100 : data.percentCompleted;
              setProgress(updatedProgress);
              setCurrentMessage(getProgressMessage(updatedProgress));

              // Fetch campaigns at milestones: 30%, 50%, 70%, 90%
              if (
                (prevProgress < 30 && data.percentCompleted >= 30) ||
                (prevProgress < 50 && data.percentCompleted >= 50) ||
                (prevProgress < 70 && data.percentCompleted >= 70) ||
                (prevProgress < 90 && data.percentCompleted >= 90)
              ) {
                dispatch(fetchCampaignsRequest());
              }
            }

            // Navigate to dashboard when progress reaches 90% or above
            if (data.status === 'completed' || data.percentCompleted >= 90) {
              dispatch(fetchCampaignsRequest());
              setTimeout(() => {
                eventSource.close();
                navigate('/dashboard');
              }, 1000);
            }
          } catch (error) {
            console.error('Error parsing SSE inprogress data:', error, event.data);
          }
        });

        eventSource.addEventListener('completed', (event: any) => {
          try {
            const data = JSON.parse(event.data);
            setProgress(100);
            setCurrentMessage(getProgressMessage(100));
            dispatch(fetchCampaignsRequest());
            setTimeout(() => {
              eventSource.close();
              navigate('/dashboard');
            }, 1000);
          } catch (error) {
            console.error('Error parsing completed event:', error);
          }
        });

        eventSource.onerror = (error) => {
          console.error('SSE error:', error);
          eventSource.close();
          setTimeout(() => navigate('/dashboard'), 2000);
        };

        return () => {
          eventSource.close();
        };
      }
    }
  }, [workflowSuccess, workflowData, navigate, restaurantId, dispatch]);

  // Update visible campaigns when campaigns data changes during processing
  useEffect(() => {
    if (isProcessing && campaigns && campaigns.length > 0) {
      const systemGeneratedCampaigns = campaigns.filter(c => c.type === 'system_generated');
      
      const campaignsWithImages = systemGeneratedCampaigns.filter(c => c.flyerUrl || c.flyer?.image?.storage_url);
      const campaignsWithoutImages = systemGeneratedCampaigns.filter(c => !(c.flyerUrl || c.flyer?.image?.storage_url));
      
      const displayCampaigns = [
        ...campaignsWithImages,
        ...(campaignsWithoutImages.length > 0 ? [campaignsWithoutImages[0]] : [])
      ];
      
      const prevCampaignsWithImages = visibleCampaigns.filter(c => c.flyerUrl || c.flyer?.image?.storage_url);
      if (campaignsWithImages.length > prevCampaignsWithImages.length) {
        setNewlyAddedIndex(campaignsWithImages.length - 1);
        setTimeout(() => setNewlyAddedIndex(null), 1000);
      }
      
      setVisibleCampaigns(displayCampaigns);
    }
  }, [campaigns, isProcessing]);

  const handleGenerate = () => {
    if (!restaurantId) return;
    dispatch(triggerWorkflowRequest({ restaurantId }));
  };

  const handleCampaignClick = (campaignId: string) => {
    if (progress < 100) {
      localStorage.setItem('campaignGenerationInProgress', 'true');
    }
    navigate(`/flyer?campaignId=${campaignId}&campaignType=system_generated`);
  };

  // Show loading animation ONLY on initial load, NOT during processing
  if (campaignsLoading && !isProcessing) {
    return (
      <div className="min-h-screen bg- flex items-center justify-center p-6"
        style={{
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 14px 0 #0000001a',
          background: '#212121',
          border: '1px solid #e0e0e021'
        }}>
        <div className="flex flex-col items-center justify-center">
          <div className="w-64 h-64">
            <DotLottieReact
              src="https://lottie.host/ba179149-4825-49a0-8d2b-2bff787478d0/9mFTTEa0AC.lottie"
              loop
              autoplay
            />
          </div>
        </div>
      </div>
    );
  }

  // Show processing UI when workflow is triggered
  if (isProcessing) {
    return (
      <CampaignProcessingView
        visibleCampaigns={visibleCampaigns}
        newlyAddedIndex={newlyAddedIndex}
        progress={progress}
        currentMessage={currentMessage}
        onCardClick={handleCampaignClick}
      />
    );
  }

  return (
    <div
      className="min-h-screen w-full px-4 py-8 md:py-12 flex items-center  relative"
      style={{
        backgroundImage: 'url(/assets/images/landing/landing-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="fixed inset-0 bg-black/40 -z-10"></div>

      <div className="relative z-10 max-w-7xl w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6 sm:mb-8 md:mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-3 sm:mb-4 md:mb-6">
            <img
              src="/assets/images/promo-logo.png"
              alt="PromoFlow Logo"
              className="h-8 sm:h-10 w-auto"
            />
          </div>

          <h2 className="text-xl md:text-xl lg:text-2xl xl:text-3xl 2xl:text-5xl text-white mb-2 sm:mb-4 md:mb-6 leading-tight">
            Grow your Restaurant with<br />
            AI-Powered Marketing
          </h2>

          <p className="text-gray-400 text-[11px] sm:text-[12px] max-w-2xl mx-auto">
            Get campaign ideas, create posters, and track performance all in one place
          </p>
        </motion.div>

        <div className="hidden md:grid md:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-8 2xl:mb-12 max-w-4xl mx-auto">
          <div className="">
            <img
              src="/assets/images/landing/ai-recommendation.png"
              alt="Smart Campaign Ideas"
              className="w-auto h-[170px] object-contain mb-3"
            />
            <h6 className='text-gray-300 font-normal mb-2'>Smart Campaign Ideas</h6>
            <p className='text-gray-400 text-[13px]'>AI suggests promotions based on trends & events</p>
          </div>

          <div className="">
            <img
              src="/assets/images/landing/smart-campaing.png"
              alt="Instant Poster Creation"
              className="w-auto h-[170px] object-contain mb-3"
            />
            <h6 className='text-gray-300 font-normal mb-2'>Instant Poster Creation</h6>
            <p className='text-gray-400 text-[13px]'>Generate ready-to-publish creatives in seconds</p>
          </div>

          <div className="">
            <img
              src="/assets/images/landing/impressions.png"
              alt="Performance Tracking"
              className="w-auto h-[170px] object-contain mb-3"
            />
            <h6 className='text-gray-300 font-normal mb-2'>Performance Tracking</h6>
            <p className='text-gray-400 text-[13px]'>Track impressions, engagement & campaign success</p>
          </div>
        </div>

        {hasRequiredAuth && showGenerateButton && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center"
          >
            <button
              onClick={handleGenerate}
              disabled={workflowLoading}
              className={`px-8 md:px-10 py-2 md:py-3 text-white text-md md:text-lg font-normal tracking-[0.040em] rounded-full shadow-2xl transition-all
                 ${workflowLoading
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-[#ee4423] cursor-pointer'
                }`}
            >
              {workflowLoading ? 'Activating...' : 'Activate'}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default GetStarted;
